import { createContext, useContext, useEffect, useState, useRef } from "react";
import api, { setCurrency, getCurrency, setTimezone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ActivityContext = createContext(null);
// Activité sélectionnée mémorisée PAR compte : le choix d'un utilisateur ne doit jamais fuir vers un autre sur le même appareil.
const storageKey = (u) => (u && u.id) ? `biashara.activity_id.${u.tenant_id}.${u.id}` : "biashara.activity_id";
// Caches isolés par tenant + utilisateur (jamais partagés entre comptes).
const actsKey = (u) => (u && u.id) ? `biashara.activities.${u.tenant_id}.${u.id}` : null;
const settingsKey = (u) => (u && u.id) ? `biashara.settings.${u.tenant_id}.${u.id}` : null;
export const GLOBAL_SCOPE = { id: "__global__", name: "Vue globale", global: true };

export function ActivityProvider({ children }) {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [current, setCurrentState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [access, setAccess] = useState(null);
  const [currency, setCurrencyState] = useState(getCurrency());
  // ≥2 utilisateurs actifs pouvant vendre pour l'activité : null = inconnu/chargement, true/false = déterminé.
  const [multiSeller, setMultiSeller] = useState(null);
  const [sellerNonce, setSellerNonce] = useState(0);
  const reloadMultiSeller = () => setSellerNonce((n) => n + 1);

  const setCurrent = (a) => {
    setCurrentState(a);
    localStorage.setItem(storageKey(user), a?.global ? "__global__" : (a?.id || ""));
  };

  const loadSettings = async () => {
    // La devise est pilotée par l'effet dédié ci-dessous (devise de l'activité), pas par le tenant.
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      try { const k = settingsKey(user); if (k) localStorage.setItem(k, JSON.stringify(data)); } catch (e) {}
    } catch (e) {
      // Hors ligne : repli sur les réglages mis en cache (devise, nom d'organisation).
      try { const k = settingsKey(user); const c = k && localStorage.getItem(k); if (c) setSettings(JSON.parse(c)); } catch (e2) {}
    }
  };

  // Access is per-activity: menu adapts to the selected activity + the user's rights.
  // Garde anti-course : seule la dernière requête lancée peut écrire `access`
  // (évite qu'un appel sans activity_id, qui renvoie can_sell=false, n'écrase le résultat scoping activité).
  const accessReqRef = useRef(0);
  const loadAccess = async (activityId) => {
    const token = ++accessReqRef.current;
    try {
      const q = activityId ? `?activity_id=${activityId}` : "";
      const { data } = await api.get(`/me/access${q}`);
      if (token === accessReqRef.current) setAccess(data);
    } catch (e) {}
  };

  const load = async () => {
    setLoading(true);
    try {
      let data = null;
      try {
        const res = await api.get("/activities");
        data = res.data;
        try { const k = actsKey(user); if (k) localStorage.setItem(k, JSON.stringify(data)); } catch (e) {}
      } catch (e) {
        // Hors ligne : repli sur la dernière liste d'activités mise en cache pour CE user+tenant.
        try { const k = actsKey(user); const c = k && localStorage.getItem(k); if (c) data = JSON.parse(c); } catch (e2) {}
        if (!data) return; // aucune donnée disponible : ne pas vider l'état existant
      }
      const active = data.filter((a) => a.status !== "archived");
      setActivities(data);
      const savedId = localStorage.getItem(storageKey(user));
      const priv = user && (user.is_owner || ["owner", "admin", "manager"].includes(user.role));
      setCurrentState((prev) => {
        if (priv && savedId === "__global__") return GLOBAL_SCOPE;
        const bySaved = active.find((a) => a.id === savedId);
        if (bySaved) return bySaved;
        if (prev && !prev.global) { const p = active.find((a) => a.id === prev.id); if (p) return p; }
        return active[0] || GLOBAL_SCOPE;
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) { load(); loadSettings(); } }, [user]);
  // Au retour du réseau : re-synchronise activités + réglages (les activités doivent réapparaître).
  useEffect(() => {
    if (!user) return;
    const onOnline = () => { load(); loadSettings(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    // eslint-disable-next-line
  }, [user]);
  // La devise affichée suit l'activité sélectionnée (repli sur la devise du tenant en vue globale)
  useEffect(() => {
    const eff = (current && !current.global && current.currency) ? current.currency : (settings.currency || null);
    if (eff) { setCurrency(eff); setCurrencyState(eff); }
  }, [current?.id, current?.global, current?.currency, settings.currency]);
  // Fuseau horaire de l'entreprise : appliqué à tous les affichages d'heure (interface, reçus, rapports).
  useEffect(() => { if (settings.timezone) setTimezone(settings.timezone); }, [settings.timezone]);
  // reload the effective module list whenever the selected activity changes.
  // On attend la résolution de l'activité (current non nul) pour ne pas lancer un appel sans
  // activity_id qui renverrait can_sell=false et masquerait à tort la caisse au vendeur.
  useEffect(() => { if (user && current) loadAccess(current.global ? null : current.id); /* eslint-disable-next-line */ }, [current?.id, current?.global, user]);
  // Détecte s'il existe ≥2 vendeurs actifs pour l'activité sélectionnée (affichage du Rapport vendeurs).
  useEffect(() => {
    if (!user || !current) { setMultiSeller(null); return; }
    if (current.global) { setMultiSeller(false); return; }
    let cancelled = false;
    setMultiSeller(null);
    api.get(`/activities/seller-count?activity_id=${current.id}`)
      .then((r) => { if (!cancelled) setMultiSeller(!!r.data?.multi); })
      .catch(() => { if (!cancelled) setMultiSeller(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [current?.id, current?.global, user, sellerNonce]);

  return (
    <ActivityContext.Provider value={{
      activities, current, setCurrent, reload: load, loading,
      settings, reloadSettings: loadSettings, currency,
      access, modules: access?.modules || null,
      multiSeller, reloadMultiSeller,
      canSell: access?.can_sell,
      sensitiveModes: access?.sensitive_modes || {},
      sellableLocationIds: access?.sellable_location_ids,
      locationsVisible: access?.locations_visible,
      assignedLocations: access?.locations || [],
      reloadAccess: () => loadAccess(current?.global ? null : current?.id),
    }}>
      {children}
    </ActivityContext.Provider>
  );
}

export const useActivity = () => useContext(ActivityContext);
